import { useContext, useState } from 'react';
import { Sparkles, Tag, Loader2, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LangContext } from '@/context/LangProvider';
import { useGetQuestionCategories } from '@/reactQuery/get';
import { useMutateSuggestCategories } from '@/reactQuery/post';
import EditCategoriesModal from '@/components/Modals/EditCategoriesModal';
import useCategoryStore from '@/store/CategoryStore';
import { ECategoryMode } from '@/enums';

interface QuestionCategoriesProps {
  projectId: string;
  questionId: string;
  questionText: string;
  isDisabled: boolean;
  /**
   * TELV1-526 — question ids are client-generated (uuid) and only exist
   * server-side after the project is saved. Both category paths hit the
   * analyzer with that id (AI Suggest immediately, Define My Own on save),
   * so until the question is persisted they can only 404. Same gate as
   * media uploads (QuestionMediaUploading).
   */
  isQuestionSaved: boolean;
}

/**
 * TELV1-186 — Design-screen component for defining categories before publishing.
 *
 * Renders on each open QuestionBox when the project is not yet published or completed.
 * Offers two paths:
 *  - "AI Suggest": calls POST /suggest and opens EditCategoriesModal pre-populated with
 *    AI-generated categories (saveMode='define', no categorisation triggered).
 *  - "Define My Own": opens EditCategoriesModal with an empty list so the researcher
 *    can write categories manually (saveMode='define').
 *
 * Second+ "AI Suggest" click shows a confirmation AlertDialog before replacing existing
 * categories, matching the UX decisions from TELV1-186.
 */
export function QuestionCategories({
  projectId,
  questionId,
  questionText,
  isDisabled,
  isQuestionSaved,
}: QuestionCategoriesProps) {
  const { lang } = useContext(LangContext);
  const { setCategoryType } = useCategoryStore();
  const [openModal, setOpenModal] = useState(false);
  const [openReplaceDialog, setOpenReplaceDialog] = useState(false);

  const { data: categoriesData, isPending } = useGetQuestionCategories(
    null,
    projectId,
    questionId,
    false,
  );

  const { mutateAsync: suggestCategories, status: suggestStatus } =
    useMutateSuggestCategories(projectId);

  const categories = categoriesData?.categories || [];
  const hasCategoriesData = categories.length > 0;
  const isSuggesting = suggestStatus === 'pending';
  // TELV1-526 — both paths need the question to exist server-side (see
  // isQuestionSaved prop doc); gate them like media uploads do.
  const isActionDisabled = isDisabled || !isQuestionSaved;

  const runSuggest = async () => {
    setCategoryType(ECategoryMode.SINGLE);
    try {
      await suggestCategories({ questionId });
      setOpenModal(true);
    } catch (error) {
      // TELV1-526 — surface the server's actual message (e.g. "Question does
      // not exist for this project") instead of a generic try-again toast.
      toast.error(getErrorMessage(error, lang.get('msg.errorPleaseTryAgain')));
    }
  };

  const handleSuggestClick = () => {
    if (hasCategoriesData) {
      // Second+ click — ask for confirmation before replacing
      setOpenReplaceDialog(true);
    } else {
      runSuggest();
    }
  };

  const handleDefineOwn = () => {
    setCategoryType(ECategoryMode.SINGLE);
    setOpenModal(true);
  };

  if (isPending) return null;

  return (
    <div className="flex flex-col gap-2 mt-2">
      <p className="text-xs font-medium text-gray-500">{lang.get('msg.categoriesForQuestion')}</p>

      {!hasCategoriesData ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSuggestClick}
              disabled={isActionDisabled || isSuggesting}
              className="flex items-center gap-1.5 text-xs"
            >
              {isSuggesting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {isSuggesting ? lang.get('msg.suggesting') : lang.get('msg.aiSuggest')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDefineOwn}
              disabled={isActionDisabled}
              className="flex items-center gap-1.5 text-xs"
            >
              <Tag size={14} />
              {lang.get('msg.defineMyOwn')}
            </Button>
          </div>
          {/* TELV1-526 — explain WHY the buttons are off for a brand-new
              question (compact hint, not a banner — the media section already
              shows a save-first banner in the same box). */}
          {!isQuestionSaved && !isDisabled && (
            <p className="flex items-center gap-1 text-xs text-amber-600">
              <Info size={12} className="shrink-0" />
              {lang.get('msg.saveQuestionBeforeCategories') ||
                'Save the question first to define categories.'}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {categories.map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border"
              style={{ borderColor: cat.color }}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: cat.color }}
              />
              {cat.label}
            </span>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSuggestClick}
            disabled={isActionDisabled || isSuggesting}
            className="flex items-center gap-1.5 text-xs h-6 px-2"
          >
            {isSuggesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {isSuggesting ? lang.get('msg.suggesting') : lang.get('msg.aiSuggest')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              // TELV1-186 — seed the category mode before opening the editor.
              // The CategoryStore resets `categoryType` to null on remount (e.g.
              // after navigating away and back, which unpublishing causes), and
              // EditCategoriesModal hides the existing category list while it's
              // null. Restore it from the saved `last_ran` so edits to existing
              // categories show them.
              setCategoryType((categoriesData?.last_ran as ECategoryMode) ?? ECategoryMode.SINGLE);
              setOpenModal(true);
            }}
            disabled={isActionDisabled}
            className="text-xs h-6 px-2"
          >
            {lang.get('msg.edit')}
          </Button>
        </div>
      )}

      {/* Confirmation dialog shown when "AI Suggest" is clicked but categories already exist */}
      <AlertDialog open={openReplaceDialog} onOpenChange={setOpenReplaceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lang.get('msg.replaceCategoriesTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{lang.get('msg.replaceCategoriesBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenReplaceDialog(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setOpenReplaceDialog(false);
                runSuggest();
              }}
            >
              {lang.get('msg.replace')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditCategoriesModal
        data={categories}
        projectId={projectId}
        questionId={questionId}
        questionText={questionText}
        openModal={openModal}
        setOpenModal={setOpenModal}
        isCategoryDataExists={hasCategoriesData}
        isLoading={false}
        saveMode="define"
      />
    </div>
  );
}
